import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import { axiosAPI as axios, setAuthToken } from 'utils';
import { startNetworkMonitor } from './network';
import { ensureOfflineAssetUrls, initializeAssetResolver, syncOfflineAssetsFromPlayData, resolveOfflineAssetUrlSync, formatBytes } from './assets';
import { flushQueuedRequests } from './queue';
import {
  cacheFamilyLogin,
  cachePlayBootstrap,
  getCachedFamilyLogin,
} from './cache';
import { getOfflineState, isOnline, subscribeOfflineState, updateOfflineState } from './state';

let initialized = false;

async function refreshOfflineFamilyAuth() {
  const token = String(localStorage.getItem('jwtToken') || '');
  const remembered = await getCachedFamilyLogin();
  const familyAccountToken = localStorage.getItem('flcMobileFamilyAccountToken') || '';
  const usernames = Array.isArray(remembered?.usernames) ? remembered.usernames.filter(Boolean) : [];

  // A bundled app can become online before React/Redux has restored its auth
  // header. Restore it here so background queue replay never depends on route timing.
  if (token && !token.endsWith('.offline')) {
    setAuthToken(token);
    try {
      const refreshed = await axios.get('/api/auth/updateToken');
      const refreshedToken = String(refreshed?.data?.token || '');
      if (refreshedToken) {
        localStorage.setItem('jwtToken', refreshedToken);
        setAuthToken(refreshedToken);
        if (remembered) await cacheFamilyLogin({ ...remembered, token: refreshedToken, offline: false });
      }
      return true;
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      if (!status) return false;
      // Expired learner JWT can still be renewed through the retained family
      // account token below without forcing the family through registration again.
    }
  }

  if (!familyAccountToken || !usernames.length) return false;

  try {
    const response = await axios.post('/api/mobile-wrapper/family-session/login', {
      familyAccountToken,
      usernames,
    });
    const result = response?.data || {};
    if (!result?.token) return false;
    localStorage.setItem('jwtToken', result.token);
    setAuthToken(result.token);
    await cacheFamilyLogin({
      token: result.token,
      usernames,
      familyId: result.familyId || remembered?.familyId || null,
      familyMemberId: result.familyMemberId || remembered?.familyMemberId || null,
      offline: false,
    });
    return true;
  } catch (error) {
    console.warn('[mobile-offline] unable to refresh family login', error?.message || error);
    return false;
  }
}

async function resumeOfflineWork() {
  if (!isOnline()) return;

  // Mark reconnect as background work immediately, before token refresh or queue
  // replay can expose a stale full-screen "Preparing assessment" phase.
  window.dispatchEvent(new CustomEvent('flc:offline-online-returned', {
    detail: { authReady: null, syncPending: true },
  }));

  const authReady = await refreshOfflineFamilyAuth();
  if (!authReady) {
    window.dispatchEvent(new CustomEvent('flc:offline-assessment-synced', {
      detail: { authReady: false, flushed: 0, pending: 0 },
    }));
    return;
  }

  (async () => {
    let queueResult = { flushed: 0, pending: 0 };
    try {
      queueResult = await flushQueuedRequests(axios);
    } catch (_) {}

    window.dispatchEvent(new CustomEvent('flc:offline-assessment-synced', {
      detail: {
        authReady: true,
        flushed: Number(queueResult?.flushed || 0),
        pending: Number(queueResult?.pending || 0),
      },
    }));

    try {
      // Reconnect/startup background work is server synchronization only.
      // Do NOT start any Placement/Custom Level asset preparation here: doing so
      // can acquire the shared asset sync lock before FlinkPlay mounts, making a
      // normal logout -> login wait behind a large Custom Level package scan.
      // Asset packages are checked/prepared by their explicit UI flows instead.
      const response = await axios.get('/api/mobile-v2/play/bootstrap');
      const playData = response?.data || null;
      if (playData) await cachePlayBootstrap(playData);
    } catch (_) {}
  })();
}

export async function initializeMobileOffline() {
  if (!isMobileAppRuntime() || initialized) return getOfflineState();
  initialized = true;
  await initializeAssetResolver();
  await startNetworkMonitor(resumeOfflineWork);
  updateOfflineState({ initialized: true });
  if (isOnline()) resumeOfflineWork();
  return getOfflineState();
}

export {
  ensureOfflineAssetUrls,
  formatBytes,
  getOfflineState,
  isOnline,
  resolveOfflineAssetUrlSync,
  subscribeOfflineState,
  syncOfflineAssetsFromPlayData,
};
